import { AxiosError } from 'axios';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ComboBox,
  DataTable,
  DataTableSkeleton,
  DatePicker,
  DatePickerInput,
  Loading,
  Modal,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tag,
} from '@carbon/react';
import { Add, Erase, Renew } from '@carbon/react/icons';
import EmptyState from 'src/components/Base/EmptyState';
import FormattedDate from 'src/components/Base/FormattedDate';
import InventoryMovementForm from 'src/components/Inventory/InventoryMovementForm';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useAllInventoryItems } from 'src/hooks/useInventoryItems';
import { useCreateInventoryMovementMutation, useInventoryMovements } from 'src/hooks/useInventoryMovements';
import { useBranches, useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { getMutationErrorMessage } from 'src/utils/error';
import {
  formatQuantity,
  getMovementTypeColor,
  getMovementTypeText,
  inventoryMovementTypeOptions,
} from 'src/utils/inventory';

const InventoryMovements = React.memo(() => {
  const { t } = useTranslation();
  const formRef = useRef<any>();
  const {
    query: { isLoading, data: movements, isRefetching, refetch },
    page,
    filters,
    setPage,
    setFilters,
  } = useInventoryMovements();
  const { isAdmin } = useAuthPermissions();
  const { data: branches } = useBranches();
  const { data: items } = useAllInventoryItems();
  const currentRestaurant = useCurrentRestaurant();
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const createMovementMutation = useCreateInventoryMovementMutation();

  const [openAddMovement, setOpenAddMovement] = useState(false);

  const handleCloseAddMovement = useCallback(() => setOpenAddMovement(false), []);

  const handleAddMovement = useCallback(() => {
    formRef.current?.resetForm();
    setOpenAddMovement(true);
  }, []);

  const handleCreate = useCallback(
    async (values) => {
      await createMovementMutation.mutateAsync({
        ...values,
        quantity: Number(values.quantity),
        date: new Date(values.date).toISOString(),
      });
      setOpenAddMovement(false);
    },
    [createMovementMutation]
  );

  const headers = [{ key: 'date', header: t('common.date') }];

  const extraHeaders = [
    { key: 'item', header: t('common.inventoryItem') },
    { key: 'type', header: t('common.type') },
    { key: 'quantity', header: t('common.quantity') },
    { key: 'note', header: t('common.notes') },
    { key: 'branch', header: t('common.branch') },
  ];

  if (isLoading) {
    return (
      <DataTableSkeleton
        columnCount={headers.length + extraHeaders.length}
        rowCount={6}
        headers={[...headers, ...extraHeaders]}
      />
    );
  }

  const getTableTitle = () => {
    if (currentRestaurantBranch?.name) {
      return t('titles.inventoryPage', {
        restaurantName: currentRestaurant?.name,
        branchName: currentRestaurantBranch?.name,
        titlePlural: t('common.inventoryMovements'),
      });
    }
    return t('titles.inventoryPageWithoutBranch', {
      restaurantName: currentRestaurant?.name,
      titlePlural: t('common.inventoryMovements'),
    });
  };

  const filteredRows = movements?.data || [];
  const submitting = createMovementMutation.isLoading;
  const createError = getMutationErrorMessage(createMovementMutation.error as AxiosError);
  const title = t('common.inventoryMovements');

  return (
    <>
      <DataTable rows={filteredRows} headers={headers}>
        {({ rows, headers, getTableProps, getHeaderProps, getToolbarProps, getRowProps }) => (
          <TableContainer title={getTableTitle()} description={`${movements?.total} ${title.toLowerCase()}`}>
            <TableToolbar {...getToolbarProps()} aria-label={t('common.dataTableToolbar')}>
              {isRefetching && <Loading className={'some-class'} small />}
              <TableToolbarContent>
                <TableToolbarSearch onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
                <ComboBox
                  items={(items?.data || []).map((item) => ({ id: item.id, text: item.name }))}
                  placeholder={`${t('common.inventoryItem')}...`}
                  size={'lg'}
                  onChange={(e) => setFilters({ ...filters, itemId: e.selectedItem?.id })}
                  className={'cds--combo-box--no-border-bottom'}
                  itemToString={(item) => (item ? item.text : '')}
                />
                <ComboBox
                  items={inventoryMovementTypeOptions}
                  placeholder={`${t('common.type')}...`}
                  size={'lg'}
                  onChange={(e) => setFilters({ ...filters, type: e.selectedItem?.id })}
                  className={'cds--combo-box--no-border-bottom'}
                  itemToString={(item) => (item ? t(item.text) : '')}
                />
                <DatePicker
                  dateFormat={'d/m/Y'}
                  datePickerType="range"
                  disabled={isAdmin}
                  value={
                    filters.startDate && filters.endDate ? [new Date(filters.startDate), new Date(filters.endDate)] : []
                  }
                  onChange={(dates) => {
                    if (dates.length > 1) {
                      setFilters({
                        ...filters,
                        startDate: dates[0]?.toISOString(),
                        endDate: dates[1]?.toISOString(),
                      });
                    }
                  }}
                >
                  <DatePickerInput disabled={isAdmin} placeholder={t('dateRanges.startDate')} />
                  <DatePickerInput disabled={isAdmin} placeholder={t('dateRanges.endDate')} />
                </DatePicker>
                {filters.startDate && filters.endDate && (
                  <Button
                    renderIcon={Erase}
                    onClick={() => setFilters({ ...filters, startDate: null, endDate: null })}
                    hasIconOnly
                    disabled={isAdmin}
                    kind="ghost"
                    iconDescription={t('common.clearDates')}
                  />
                )}
                <Button
                  renderIcon={Renew}
                  onClick={refetch}
                  hasIconOnly
                  kind="secondary"
                  iconDescription={t('common.refresh')}
                />
                <Button renderIcon={Add} onClick={handleAddMovement}>
                  {t('actions.recordMovement')}
                </Button>
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {[...headers, ...extraHeaders].map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const item = items?.data.find((entity) => entity.id === filteredRows[index]?.itemId);
                  return (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      <TableCell>
                        {filteredRows[index] && (
                          <FormattedDate date={filteredRows[index]?.date} format={'DD/MM/YYYY HH:mm'} />
                        )}
                      </TableCell>
                      <TableCell>{filteredRows[index] && item?.name}</TableCell>
                      <TableCell>
                        {filteredRows[index] && (
                          <Tag type={getMovementTypeColor(filteredRows[index]?.type)} size="sm">
                            {t(getMovementTypeText(filteredRows[index]?.type))}
                          </Tag>
                        )}
                      </TableCell>
                      <TableCell>
                        {filteredRows[index] &&
                          (filteredRows[index]?.quantity > 0 ? '+' : '') +
                            formatQuantity(filteredRows[index]?.quantity, item?.unit || '')}
                      </TableCell>
                      <TableCell>{filteredRows[index]?.note}</TableCell>
                      <TableCell>
                        {filteredRows[index] &&
                          branches?.data.find((branch) => branch.id === filteredRows[index]?.restaurantBranchId)?.name}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {rows.length === 0 && <EmptyState />}
          </TableContainer>
        )}
      </DataTable>
      <Pagination
        backwardText={t('common.previousPage')}
        forwardText={t('common.nextPage')}
        itemsPerPageText={`${t('common.itemsPerPage')}:`}
        onChange={({ pageSize, page }) => {
          setPage(page);
          setFilters({ ...filters, limit: pageSize });
        }}
        // itemRangeText, not itemText: Carbon only calls itemText when `pagesUnknown` is set, which
        // this bar never sets, so the translated text was unreachable and the range rendered
        // Carbon's English default. Same fix, and the same key, as CrudPage.
        itemRangeText={(min, max, total) => t('common.itemRange', { min, max, total })}
        pageRangeText={(_current, total) =>
          `${t('common.of')} ${total} ${total === 1 ? t('common.page') : t('common.pages')}`
        }
        page={page}
        pageSize={filters.limit}
        pageSizes={[20, 50, 100]}
        size="lg"
        totalItems={movements?.total}
      />
      <Modal
        open={openAddMovement}
        size={'sm'}
        modalHeading={t('actions.recordMovement')}
        modalLabel={t('titles.inventoryPageWithoutBranch', {
          restaurantName: currentRestaurant?.name,
          titlePlural: title,
        })}
        primaryButtonText={submitting ? `${t('common.loading')}...` : t('actions.create')}
        secondaryButtonText={t('actions.cancel')}
        onSecondarySubmit={handleCloseAddMovement}
        onRequestClose={handleCloseAddMovement}
        primaryButtonDisabled={submitting}
        onRequestSubmit={() => formRef.current?.submitForm()}
      >
        <div style={{ minHeight: 500 }}>
          {openAddMovement && (
            <InventoryMovementForm errorMessage={createError} ref={formRef} onSubmit={handleCreate} />
          )}
        </div>
      </Modal>
    </>
  );
});

export default InventoryMovements;
